/**
 * How the console's records point at one another.
 *
 * Monitoring is a walk — a user, one of their alarms, the event it produced,
 * back to the alarm, back to the user — and a walk needs two things a plain
 * detail sheet does not have: an address, so a refresh or a shared link lands
 * on the same record, and a memory of where the operator came from.
 *
 * Both live in the query string. `?user=…`, `?alarm=…` and `?event=…` say
 * which record is open on the page that owns it; `?from=…` carries the steps
 * behind it. Keeping the trail in the url rather than in React state is the
 * whole trick: reloading the page, or sending the link to a colleague, hands
 * over the way back as well as the destination.
 */

import { type ReactNode } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui';
import { useT } from './i18n';

export type RecordKind = 'user' | 'alarm' | 'event';

export interface RecordRef {
  kind: RecordKind;
  id: string;
}

/** The steps behind the record currently on screen, oldest first. */
export type Trail = RecordRef[];

/**
 * Long enough for the round trip this console is built around, short enough
 * that a url stays a url. Older steps fall off the front.
 */
const MAX_TRAIL = 6;

const TRAIL_SEPARATOR = '~';

/** The page each kind of record is opened on, and the param that opens it. */
const ROUTES: Record<RecordKind, { path: string; param: string }> = {
  user: { path: '/users', param: 'user' },
  // An event has no list of its own any more — it is only ever read in the
  // context of the alarm that produced it, so it opens over the alarm page.
  alarm: { path: '/alarms', param: 'alarm' },
  event: { path: '/alarms', param: 'event' },
};

function isRecordKind(value: string): value is RecordKind {
  return value === 'user' || value === 'alarm' || value === 'event';
}

function encodeRef(ref: RecordRef): string {
  return `${ref.kind}:${ref.id}`;
}

function parseRef(value: string): RecordRef | null {
  const separator = value.indexOf(':');
  if (separator <= 0) return null;

  const kind = value.slice(0, separator);
  const id = value.slice(separator + 1);
  if (!id || !isRecordKind(kind)) return null;

  return { kind, id };
}

export function parseTrail(value: string | null): Trail {
  if (!value) return [];
  return value
    .split(TRAIL_SEPARATOR)
    .map(parseRef)
    .filter((ref): ref is RecordRef => ref !== null)
    .slice(-MAX_TRAIL);
}

function encodeTrail(trail: Trail): string {
  return trail.map(encodeRef).join(TRAIL_SEPARATOR);
}

/**
 * Extends [trail] with the record being left behind.
 *
 * Revisiting somewhere the trail already contains rewinds to it rather than
 * appending, so the loop this console is designed around — user, alarm, event,
 * alarm, user — ends where it started instead of leaving a breadcrumb per lap.
 */
export function pushTrail(trail: Trail, ref: RecordRef): Trail {
  const seen = trail.findIndex(
    (step) => step.kind === ref.kind && step.id === ref.id,
  );
  if (seen >= 0) return trail.slice(0, seen);

  return [...trail, ref].slice(-MAX_TRAIL);
}

/** The url that opens [ref], carrying [trail] as the way back. */
export function recordPath(ref: RecordRef, trail: Trail = []): string {
  const route = ROUTES[ref.kind];
  const params = new URLSearchParams({ [route.param]: ref.id });
  if (trail.length > 0) params.set('from', encodeTrail(trail));

  return `${route.path}?${params.toString()}`;
}

/** The alarm list, scoped to one device's alarms. */
export function deviceAlarmsPath(deviceId: string, trail: Trail = []): string {
  const params = new URLSearchParams({ deviceId });
  if (trail.length > 0) params.set('from', encodeTrail(trail));

  return `/alarms?${params.toString()}`;
}

// ── Hooks ───────────────────────────────────────────────────────────────────

/** The trail that led to whatever this page currently has open. */
export function useTrail(): Trail {
  const [params] = useSearchParams();
  return parseTrail(params.get('from'));
}

/**
 * The trail a link *out of* the record on screen should carry — the current
 * one appended, so the destination can offer a way back to it.
 */
export function useOnwardTrail(kind: RecordKind, id: string): Trail {
  return pushTrail(useTrail(), { kind, id });
}

/** The params that address a record, as opposed to the page under it. */
const RECORD_PARAMS = ['user', 'alarm', 'event', 'from'];

/**
 * Opens a record, keeping the page's own url state — its tab, its device
 * scope — when the destination is on the page the operator is already on.
 * Closing the sheet again then puts them back on the list they opened it
 * from, still searched, rather than on the page's default view.
 */
export function useOpenRecord(): (ref: RecordRef, trail?: Trail) => void {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { pathname } = useLocation();

  return (ref, trail = []) => {
    const path = recordPath(ref, trail);
    if (!path.startsWith(`${pathname}?`)) {
      void navigate(path);
      return;
    }

    const next = new URLSearchParams(params);
    for (const key of RECORD_PARAMS) next.delete(key);
    new URLSearchParams(path.slice(pathname.length + 1)).forEach(
      (value, key) => next.set(key, value),
    );
    void navigate(`${pathname}?${next.toString()}`);
  };
}

// ── Components ──────────────────────────────────────────────────────────────

/**
 * The way back to the previous record, or nothing at all.
 *
 * Labelled with what it returns to rather than a bare "back": an operator four
 * records deep has stopped tracking the order they arrived in, and "Back to
 * the user" answers the question the arrow alone raises. Popping the trail as
 * it goes means the button keeps working the whole way home.
 */
export function BackButton({ trail }: { trail: Trail }) {
  const t = useT();
  const open = useOpenRecord();

  const previous = trail.at(-1);
  if (!previous) return null;

  return (
    <Button
      icon={<ArrowLeft size={15} />}
      onClick={() => open(previous, trail.slice(0, -1))}
    >
      {t(`nav.backTo.${previous.kind}`)}
    </Button>
  );
}

/** A button that opens a related record, carrying the trail with it. */
export function RecordLink({
  to,
  trail,
  icon,
  children,
}: {
  to: RecordRef;
  trail: Trail;
  icon?: ReactNode;
  children: ReactNode;
}) {
  const open = useOpenRecord();

  return (
    <Button icon={icon} onClick={() => open(to, trail)}>
      {children}
    </Button>
  );
}

/**
 * The strip of links at the top of every detail sheet: where you came from on
 * the left, where you can go next beside it. Same place on every screen, so
 * moving between records is one habit rather than three.
 */
export function RecordNav({
  trail,
  children,
}: {
  trail: Trail;
  children?: ReactNode;
}) {
  if (trail.length === 0 && !children) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line pb-4">
      <BackButton trail={trail} />
      {children}
    </div>
  );
}
